"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, CalendarDayButton } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { ReservationAvailabilityPicker } from "@/components/reservation-availability-picker";
import { ReservationConfirmation } from "@/components/reservation-confirmation";
import type { ReservationCalendarVenue } from "@/lib/reservation-calendar-links";
import { cn, getTodayLocal } from "@/lib/utils";
import { formatTime12h } from "@convex/lib/reservationTime";
import { analytics, getLeadAttribution, getPartySizeBucket } from "@/lib/analytics";
import { sendRestaurantReservationGoogleAdsConversion } from "@/lib/google-ads-conversions";
import { toast } from "sonner";
import {
  Users,
  CalendarDays,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  Phone,
} from "lucide-react";
import {
  useDayAvailabilityMap,
  usePrefetchDayAvailability,
} from "@/lib/reservation-availability-hooks";
import {
  loadGuestMemory,
  saveGuestMemory,
  clearGuestMemory,
} from "@/lib/reservation-guest-memory";

// ─── Types ───

type SeatingPreference = "indoor" | "patio" | "bar" | "no_preference";
type Occasion = "birthday" | "anniversary" | "date_night" | "business" | "celebration" | "none";

type Step = "party_size" | "date" | "time" | "details" | "confirm";

const STEPS: Step[] = ["party_size", "date", "time", "details"];

const STEP_META: Record<Step, { label: string; icon: typeof Users; number: number }> = {
  party_size: { label: "Party Size", icon: Users, number: 1 },
  date: { label: "Date", icon: CalendarDays, number: 2 },
  time: { label: "Time", icon: Clock, number: 3 },
  details: { label: "Details", icon: User, number: 4 },
  confirm: { label: "Confirmed", icon: User, number: 5 },
};

const PARTY_SIZES = Array.from({ length: 12 }, (_, i) => i + 1);

const OCCASIONS: { value: Occasion; label: string }[] = [
  { value: "none", label: "No special occasion" },
  { value: "birthday", label: "Birthday" },
  { value: "anniversary", label: "Anniversary" },
  { value: "date_night", label: "Date Night" },
  { value: "business", label: "Business Dinner" },
  { value: "celebration", label: "Celebration" },
];

const SEATING_OPTIONS: { value: SeatingPreference; label: string }[] = [
  { value: "no_preference", label: "No preference" },
  { value: "indoor", label: "Indoor" },
  { value: "patio", label: "Patio" },
  { value: "bar", label: "Bar" },
];

const leadInputClass =
  "h-11 rounded-xl border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 text-[var(--roma-espresso)] placeholder:text-[var(--roma-espresso)]/50 focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[var(--roma-saffron)]/35";

const leadSelectClass =
  "h-11 w-full rounded-xl border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 text-[var(--roma-espresso)] data-[placeholder]:text-[var(--roma-espresso)]/50 [&_[data-slot=select-value]]:text-[var(--roma-espresso)] focus:ring-[var(--roma-saffron)]/35 focus:ring-offset-0";

const leadFieldLabelClass =
  "text-xs font-medium text-[var(--roma-espresso)] sm:text-sm";

const leadOptionalHintClass =
  "text-xs font-normal text-[var(--roma-espresso)]/72";

const leadFieldErrorClass =
  "text-xs font-medium text-destructive";

const leadInputErrorClass =
  "border-destructive ring-1 ring-destructive/25 focus-visible:border-destructive focus-visible:ring-destructive/30";

const smsConsentClass =
  "flex min-h-10 items-start gap-2 rounded-lg border border-[var(--roma-parchment-line)]/80 bg-[var(--roma-linen)]/30 p-2 text-[10px] leading-snug text-[var(--roma-espresso)]/72 sm:min-h-11 sm:gap-3 sm:p-3.5 sm:text-xs sm:leading-5";

const stepTitleClass =
  "font-serif text-lg font-normal tracking-tight text-[var(--roma-espresso)] sm:text-3xl";

const stepSubtitleClass =
  "mt-0 text-[11px] leading-4 text-[var(--roma-espresso)]/64 sm:mt-2 sm:text-sm sm:leading-6";

const compactStepTitleClass =
  "font-serif text-lg font-normal tracking-tight text-[var(--roma-espresso)] sm:text-3xl";

const compactStepSubtitleClass =
  "mt-0 text-[11px] leading-snug text-[var(--roma-espresso)]/78 sm:mt-2 sm:text-sm sm:leading-6";

const mobileCalendarClass =
  "rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 p-1 [--cell-size:2.5rem] sm:p-3 sm:[--cell-size:--spacing(8)]";

// Short directional enter on each wizard step so forward motion reads as
// progress instead of an instant content swap. Reduced-motion safe.
const stepEnterClass =
  "animate-in fade-in slide-in-from-right-2 duration-200 ease-out motion-reduce:animate-none";

function ReservationCalendarDayButton({
  className,
  ...props
}: React.ComponentProps<typeof CalendarDayButton>) {
  return (
    <CalendarDayButton
      className={cn(
        className,
        "text-sm font-medium text-[var(--roma-espresso)] [&>span]:text-sm [&>span]:opacity-100",
        "hover:bg-[var(--roma-linen)]/80",
        "data-[selected-single=true]:!bg-[var(--roma-saffron)] data-[selected-single=true]:font-semibold data-[selected-single=true]:!text-[var(--roma-espresso)] data-[selected-single=true]:ring-2 data-[selected-single=true]:ring-[var(--roma-terracotta)]/45",
        "disabled:text-[var(--roma-espresso)]/32 disabled:opacity-100",
      )}
      {...props}
    />
  );
}

function StepFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 -mx-2 mt-1.5 border-t border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] px-2 py-2 sm:static sm:mx-0 sm:mt-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:pt-2">
      {children}
    </div>
  );
}

function getDateOffsetDays(date: Date, today: Date) {
  const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return Math.round((selectedDate.getTime() - todayDate.getTime()) / 86_400_000);
}

function addLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Upcoming Friday (or Saturday if Friday collides with Tonight/Tomorrow), kept
// within the bookable window. Returns null when both fall outside the window,
// would duplicate another quick-pick chip, or when today already IS the
// weekend (Fri/Sat/Sun) and "This weekend" would confusingly mean next week.
function getWeekendDate(today: Date, tomorrow: Date, maxDate: Date): Date | null {
  if ([0, 5, 6].includes(today.getDay())) return null;
  const fridayOffset = (5 - today.getDay() + 7) % 7;
  for (const offset of [fridayOffset, fridayOffset + 1]) {
    const candidate = addLocalDays(today, offset);
    if (candidate > maxDate) continue;
    if (isSameLocalDay(candidate, today) || isSameLocalDay(candidate, tomorrow)) continue;
    return candidate;
  }
  return null;
}

function formatLocalDateStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}


const PHONE_OR_EMAIL_REQUIRED = "Enter a phone number or an email";

// Format a phone field into (702) 555-0123 as the guest types. Keeps US 10-digit
// shape and strips a leading country-code 1 for display. Validation and the
// Convex mutation both strip non-digits, so storing the formatted value is safe.
function formatPhoneInput(value: string): string {
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatRestaurantPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

// ─── Component ───

export function ReservationForm({
  className,
  locationId,
  restaurantPhone,
  smsConsent: smsConsentCopy,
  venue,
}: {
  className?: string;
  locationId?: string;
  restaurantPhone: string;
  /**
   * Tenant SMS consent wording. It must match the registered 10DLC campaign's
   * message flow word for word. Absent = the default copy below.
   */
  smsConsent?: { text: string; privacyHref: string; termsHref: string };
  venue: ReservationCalendarVenue;
}) {
  // Wizard state
  const [step, setStep] = useState<Step>("party_size");

  // Booking data
  const [partySize, setPartySize] = useState<number>(2);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("");

  // Customer details
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [occasion, setOccasion] = useState<Occasion>("none");
  const [seatingPreference, setSeatingPreference] = useState<SeatingPreference>("no_preference");
  const [specialRequests, setSpecialRequests] = useState("");

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const hasStartedRef = useRef(false);
  const viewedStepsRef = useRef<Set<Step>>(new Set());

  // Convenience UI state
  const [showPreferences, setShowPreferences] = useState(false);
  const [prefilledFromMemory, setPrefilledFromMemory] = useState(false);
  const prefillAttemptedRef = useRef(false);

  const createReservation = useMutation(api.reservations.create);

  // ─── Derived values ───

  const dateStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    : "";
  const restaurantPhoneDisplay = formatRestaurantPhone(restaurantPhone);

  const todayStr = getTodayLocal();

  // Calendar bounds: today through 30 days out
  const todayParts = todayStr.split("-").map(Number);
  const todayDate = new Date(todayParts[0], todayParts[1] - 1, todayParts[2]);
  const maxDate = new Date(todayDate.getTime() + 30 * 24 * 60 * 60 * 1000);

  const tomorrowDate = addLocalDays(todayDate, 1);
  const tomorrowStr = formatLocalDateStr(tomorrowDate);

  // Warm the day-availability cache for the most-likely picks before the guest
  // reaches the time step. Stub per the availability-hooks contract for now.
  usePrefetchDayAvailability([todayStr, tomorrowStr], partySize, locationId);

  // Availability-aware calendar: per-day open/limited/full for the visible
  // month so guests never tap into a dead date. undefined while loading means
  // the calendar renders without availability styling.
  const [calendarMonth, setCalendarMonth] = useState<Date>(todayDate);
  const monthStartStr = formatLocalDateStr(
    new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1),
  );
  const dayAvailabilityMap = useDayAvailabilityMap(monthStartStr, partySize, locationId);
  const { fullDays, limitedDays } = useMemo(() => {
    if (!dayAvailabilityMap) return { fullDays: [], limitedDays: [] };
    const toLocalDate = (d: string) => new Date(d + "T12:00:00");
    const entries = Object.entries(dayAvailabilityMap);
    return {
      fullDays: entries.filter(([, s]) => s === "full").map(([d]) => toLocalDate(d)),
      limitedDays: entries.filter(([, s]) => s === "limited").map(([d]) => toLocalDate(d)),
    };
  }, [dayAvailabilityMap]);

  // Quick-pick chips for the date step. Each is only offered when its date is
  // bookable, and "This weekend" is hidden if it would duplicate another chip.
  const quickPicks: { label: string; date: Date }[] = [];
  if (todayDate <= maxDate) quickPicks.push({ label: "Tonight", date: todayDate });
  if (tomorrowDate <= maxDate) quickPicks.push({ label: "Tomorrow", date: tomorrowDate });
  const weekendDate = getWeekendDate(todayDate, tomorrowDate, maxDate);
  if (weekendDate) quickPicks.push({ label: "This weekend", date: weekendDate });

  // Step index for progress
  const stepIndex = STEPS.indexOf(step);

  // Preferences disclosure opens automatically when a non-default value exists.
  const preferencesOpen =
    showPreferences || occasion !== "none" || seatingPreference !== "no_preference";

  useEffect(() => {
    if (viewedStepsRef.current.has(step)) return;

    viewedStepsRef.current.add(step);
    analytics.formStepViewed("reservation", step, STEP_META[step].number);
  }, [step]);

  // When the details step first opens with an empty contact block, prefill from
  // a remembered guest. Runs once per form so live typing is never overwritten.
  useEffect(() => {
    if (step !== "details") return;
    if (prefillAttemptedRef.current) return;
    prefillAttemptedRef.current = true;

    if (customerName || customerPhone || customerEmail) return;

    const memory = loadGuestMemory();
    if (!memory || (!memory.name && !memory.phone && !memory.email)) return;

    setCustomerName(memory.name);
    setCustomerPhone(memory.phone);
    setCustomerEmail(memory.email);
    // A campaign-registered consent box must start unchecked on every booking.
    if (!smsConsentCopy) setSmsConsent(memory.smsConsent);
    setPrefilledFromMemory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function markFormStarted() {
    if (hasStartedRef.current) return;

    hasStartedRef.current = true;
    analytics.formStarted({
      category: "form",
      form_type: "reservation",
      source: "reservation_form",
    });
    analytics.reservationStarted();
  }

  function getReservationFunnelProperties(
    properties: Record<string, string | number | boolean | undefined> = {},
  ) {
    return {
      form_type: "reservation",
      source: "reservation_form",
      party_size_bucket: getPartySizeBucket(partySize),
      ...properties,
    };
  }

  function trackReservationValidationFailure(
    fieldKey: string,
    errorType: string,
  ) {
    analytics.formValidationFailed(
      getReservationFunnelProperties({
        step: "details",
        field_group: "contact",
        field_key: fieldKey,
        error_type: errorType,
      }),
    );
  }

  // ─── Navigation ───

  function goNext() {
    const currentIdx = STEPS.indexOf(step);
    if (currentIdx < STEPS.length - 1) {
      setStep(STEPS[currentIdx + 1]);
    }
  }

  function goBack() {
    const currentIdx = STEPS.indexOf(step);
    if (currentIdx > 0) {
      setStep(STEPS[currentIdx - 1]);
    }
  }

  // Shared by the calendar and the quick-pick chips so both fire the same
  // analytics and auto-advance to the time step identically.
  function selectDateAndAdvance(day: Date) {
    markFormStarted();
    setSelectedDate(day);
    analytics.dateSelected("reservation", {
      source: "reservation_form",
      date_offset_days: getDateOffsetDays(day, todayDate),
      day_of_week: day.toLocaleDateString("en-US", { weekday: "long" }),
    });
    // Clear time when date changes.
    setSelectedTime("");
    // Single-choice step: auto-advance so the guest doesn't need a redundant
    // Continue tap (Back still available).
    setStep("time");
  }

  function handleClearGuestMemory() {
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setSmsConsent(false);
    setPrefilledFromMemory(false);
    setErrors((prev) => ({ ...prev, name: "", phone: "", email: "" }));
    clearGuestMemory();
  }

  // ─── Validation ───

  function validateDetails(): boolean {
    const newErrors: Record<string, string> = {};
    const validationFailures: Array<{
      fieldKey: string;
      errorType: string;
    }> = [];

    if (!customerName.trim()) {
      newErrors.name = "Name is required";
      validationFailures.push({
        fieldKey: "name",
        errorType: "missing_required",
      });
    } else if (customerName.trim().length < 2) {
      newErrors.name = "Please enter your full name";
      validationFailures.push({
        fieldKey: "name",
        errorType: "too_short",
      });
    }

    // A phone or an email is enough; the guest picks how we reach them.
    if (!customerPhone.trim() && !customerEmail.trim()) {
      newErrors.phone = PHONE_OR_EMAIL_REQUIRED;
      newErrors.email = PHONE_OR_EMAIL_REQUIRED;
      validationFailures.push({
        fieldKey: "phone_or_email",
        errorType: "missing_required",
      });
    }

    if (customerPhone.trim()) {
      const digits = customerPhone.replace(/\D/g, "");
      if (digits.length < 10) {
        newErrors.phone = "Please enter a valid phone number";
        validationFailures.push({
          fieldKey: "phone",
          errorType: "invalid_format",
        });
      }
    }

    if (customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerEmail.trim())) {
        newErrors.email = "Please enter a valid email address";
        validationFailures.push({
          fieldKey: "email",
          errorType: "invalid_format",
        });
      }
    }

    setErrors(newErrors);
    validationFailures.forEach(({ fieldKey, errorType }) =>
      trackReservationValidationFailure(fieldKey, errorType),
    );
    return Object.keys(newErrors).length === 0;
  }

  // ─── Submit ───

  async function handleSubmit() {
    markFormStarted();
    analytics.formAttempted(getReservationFunnelProperties({ step: "details" }));
    if (!validateDetails()) return;

    setIsSubmitting(true);
    try {
      const result = await createReservation({
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        smsConsent: smsConsent && Boolean(customerPhone.trim()),
        customerEmail: customerEmail.trim() || undefined,
        date: dateStr,
        time: selectedTime,
        partySize,
        seatingPreference,
        specialRequests: specialRequests.trim() || undefined,
        occasion,
        locationId,
        attribution: getLeadAttribution("reservation_form"),
      });

      setConfirmationCode(result.confirmationCode);
      setStep("confirm");
      saveGuestMemory({
        name: customerName.trim(),
        phone: customerPhone.trim(),
        email: customerEmail.trim(),
        smsConsent,
      });
      const leadProperties = {
        lead_id: result.reservationId,
        lead_type: "reservation",
        source: "reservation_form",
        party_size_bucket: getPartySizeBucket(partySize),
      };
      analytics.formSubmitSucceeded(
        getReservationFunnelProperties({ step: "submit", ...leadProperties }),
      );
      analytics.reservationCompleted(leadProperties);
      analytics.leadCaptured("reservation", "reservation_form", leadProperties);
      sendRestaurantReservationGoogleAdsConversion({
        transactionId: result.reservationId,
        email: customerEmail.trim(),
        phone: customerPhone.trim(),
      });
      toast.success("Reservation confirmed!");
    } catch (error) {
      analytics.formSubmitFailed(
        getReservationFunnelProperties({
          step: "submit",
          error_type: error instanceof Error ? "server_error" : "unknown_error",
        }),
      );
      const message = error instanceof Error ? error.message : "Failed to create reservation. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  // ─── Reset ───

  function handleReset() {
    setStep("party_size");
    setPartySize(2);
    setSelectedDate(undefined);
    setSelectedTime("");
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setSmsConsent(false);
    setOccasion("none");
    setSeatingPreference("no_preference");
    setSpecialRequests("");
    setConfirmationCode(null);
    setErrors({});
    setShowPreferences(false);
    setPrefilledFromMemory(false);
    prefillAttemptedRef.current = false;
  }

  // ─── Confirmation step ───

  if (step === "confirm" && confirmationCode) {
    return (
      <div className={className}>
        <ReservationConfirmation
          confirmationCode={confirmationCode}
          date={dateStr}
          time={selectedTime}
          partySize={partySize}
          customerName={customerName}
          venue={venue}
          onReset={handleReset}
        />
      </div>
    );
  }

  // ─── Render ───

  return (
    <div
      className={cn("space-y-1.5 text-[var(--roma-espresso)] sm:space-y-6", className)}
      onFocusCapture={markFormStarted}
      onPointerDownCapture={markFormStarted}
    >
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-1 sm:gap-2">
        {STEPS.map((s, i) => {
          const meta = STEP_META[s];
          const isActive = i === stepIndex;
          const isComplete = i < stepIndex;

          return (
            <div key={s} className="flex items-center gap-1 sm:gap-2">
              {i > 0 && (
                <div
                  className={cn(
                    "h-px w-1.5 transition-colors sm:w-6 md:w-8",
                    isComplete
                      ? "bg-[var(--roma-terracotta)]"
                      : "bg-[var(--roma-parchment-line)]"
                  )}
                />
              )}
              <button
                type="button"
                aria-label={meta.label}
                aria-current={isActive ? "step" : undefined}
                onClick={() => {
                  if (isComplete) setStep(s);
                }}
                disabled={!isComplete}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors sm:h-11 sm:w-11 sm:text-xs",
                  isActive &&
                    "border-[var(--roma-terracotta)] bg-[var(--roma-saffron)] text-[var(--roma-espresso)] ring-2 ring-[var(--roma-saffron)]/30",
                  isComplete &&
                    "cursor-pointer border-[var(--roma-terracotta)] bg-[var(--roma-linen)] text-[var(--roma-terracotta)] hover:bg-[var(--roma-bone)]",
                  !isActive &&
                    !isComplete &&
                    "border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] text-[var(--roma-espresso)]/45"
                )}
              >
                {meta.number}
              </button>
            </div>
          );
        })}
      </div>

      {/* Mobile orientation: the dots alone are unlabeled numbers. */}
      <p className="text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)] sm:hidden">
        Step {STEP_META[step].number} of {STEPS.length} &middot; {STEP_META[step].label}
      </p>

      <Card className="border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] py-2 shadow-none sm:py-5 sm:shadow-[0_24px_70px_-56px_oklch(0.24_0.03_55_/_0.35)]">
        <CardContent className="px-2 pt-0 sm:px-6">
          {/* ── Step 1: Party Size ── */}
          {step === "party_size" && (
            <div className={cn("space-y-2 sm:space-y-4", stepEnterClass)}>
              <div className="text-center">
                <h3 className={stepTitleClass}>
                  How many guests?
                </h3>
                <p className={stepSubtitleClass}>
                  Choose who is coming.
                </p>
              </div>
              <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 sm:gap-2">
                {PARTY_SIZES.map((size) => (
                  <Button
                    key={size}
                    type="button"
                    variant={partySize === size ? "default" : "outline"}
                    className={cn(
                      "min-h-11 rounded-lg border-[var(--roma-parchment-line)] text-sm font-semibold transition-colors active:scale-[0.98] sm:h-12 sm:text-base",
                      partySize === size
                        ? "border-[var(--roma-terracotta)] bg-[var(--roma-saffron)] text-[var(--roma-espresso)] hover:bg-[var(--roma-saffron-deep)]"
                        : "bg-[var(--roma-linen)]/25 text-[var(--roma-espresso)] hover:border-[var(--roma-terracotta)] hover:bg-[var(--roma-linen)]"
                    )}
                    onClick={() => {
                      markFormStarted();
                      setPartySize(size);
                      analytics.partySizeSelected("reservation", size, {
                        source: "reservation_form",
                      });
                      // Clear time if changing party size (availability changes)
                      setSelectedTime("");
                    }}
                  >
                    {size}
                  </Button>
                ))}
              </div>
              {partySize >= 8 && (
                <p className="text-center text-[11px] leading-4 text-[var(--roma-terracotta)] sm:text-xs sm:leading-5">
                  For parties of 8 or more, please also call ahead at{" "}
                  <a
                    href={`tel:${restaurantPhone}`}
                    className="inline-flex min-h-11 items-center font-medium underline"
                    onClick={() =>
                      analytics.phoneClicked({
                        location: "reservation_large_party_notice",
                        cta_text: "Call restaurant",
                        destination_url: `tel:${restaurantPhone}`,
                        lead_type: "reservation",
                      })
                    }
                  >
                    {restaurantPhoneDisplay}
                  </a>
                </p>
              )}
              <StepFooter>
                <div className="flex justify-end">
                  <Button className="roma-cta min-h-10 rounded-full px-4 sm:min-h-11" onClick={goNext}>
                    Continue
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </StepFooter>
            </div>
          )}

          {/* ── Step 2: Date ── */}
          {step === "date" && (
            <div className={cn("space-y-1 sm:space-y-4", stepEnterClass)}>
              <div className="text-center">
                <h3 className={compactStepTitleClass}>
                  Pick a date
                </h3>
                <p className={cn(compactStepSubtitleClass, "hidden sm:block")}>
                  Reservations available up to 30 days ahead
                </p>
              </div>
              {quickPicks.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                  {quickPicks.map((pick) => (
                    <button
                      key={pick.label}
                      type="button"
                      onClick={() => selectDateAndAdvance(pick.date)}
                      className="inline-flex min-h-9 items-center rounded-full border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/40 px-3 text-xs font-medium text-[var(--roma-espresso)] transition-colors hover:border-[var(--roma-terracotta)] sm:text-sm"
                    >
                      {pick.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(day) => {
                    if (day) {
                      selectDateAndAdvance(day);
                      return;
                    }
                    // Deselect (tapping the active day): clear without advancing.
                    markFormStarted();
                    setSelectedDate(undefined);
                    setSelectedTime("");
                  }}
                  disabled={[
                    (day: Date) => day < todayDate || day > maxDate,
                    ...fullDays,
                  ]}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  modifiers={{ rezFull: fullDays, rezLimited: limitedDays }}
                  modifiersClassNames={{
                    rezFull: "line-through text-[var(--roma-espresso)]/35",
                    rezLimited: "[&_button]:font-semibold [&_button]:text-[var(--roma-terracotta)]",
                  }}
                  className={mobileCalendarClass}
                  classNames={{
                    month: "flex w-full flex-col gap-1 sm:gap-4",
                    week: "mt-0 flex w-full sm:mt-2",
                    weekdays:
                      "flex border-b border-[var(--roma-parchment-line)]/70 pb-0.5 sm:pb-1",
                    weekday:
                      "flex-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--roma-espresso)]/75 sm:text-[11px]",
                    caption_label: "text-xs font-semibold text-[var(--roma-espresso)] sm:text-sm",
                    button_previous:
                      "min-h-9 min-w-9 text-[var(--roma-espresso)] hover:bg-[var(--roma-linen)]/70 sm:min-h-11 sm:min-w-11",
                    button_next:
                      "min-h-9 min-w-9 text-[var(--roma-espresso)] hover:bg-[var(--roma-linen)]/70 sm:min-h-11 sm:min-w-11",
                    disabled: "text-[var(--roma-espresso)]/28",
                    outside: "text-[var(--roma-espresso)]/35",
                    today:
                      "rounded-md bg-[var(--roma-linen)]/70 ring-1 ring-[var(--roma-terracotta)]/60",
                  }}
                  components={{
                    DayButton: ReservationCalendarDayButton,
                  }}
                />
              </div>
              <StepFooter>
                <div className="flex justify-between">
                  <Button variant="ghost" className="min-h-10 rounded-full px-3 text-[var(--roma-espresso)]/72 hover:bg-[var(--roma-linen)] sm:min-h-11" onClick={goBack}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                  <Button className="roma-cta min-h-10 rounded-full px-4 sm:min-h-11" onClick={goNext} disabled={!selectedDate}>
                    Continue
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </StepFooter>
            </div>
          )}

          {/* ── Step 3: Time ── */}
          {step === "time" && (
            <div className={cn("space-y-1.5 sm:space-y-4", stepEnterClass)}>
              <div className="text-center">
                <h3 className={compactStepTitleClass}>
                  Choose a time
                </h3>
                <p className={compactStepSubtitleClass}>
                  {selectedDate
                    ? selectedDate.toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                      })
                    : ""}{" "}
                  &middot; {partySize} {partySize === 1 ? "guest" : "guests"}
                </p>
              </div>
              {dateStr && (
                <ReservationAvailabilityPicker
                  date={dateStr}
                  partySize={partySize}
                  selectedTime={selectedTime}
                  locationId={locationId}
                  onTimeSelected={(time) => {
                    markFormStarted();
                    setSelectedTime(time);
                    analytics.track("time_selected", {
                      category: "form",
                      form_type: "reservation",
                      source: "reservation_form",
                      time_slot: time,
                      party_size: partySize,
                    });
                    // Single-choice step: auto-advance to details.
                    setStep("details");
                  }}
                />
              )}
              <StepFooter>
                <div className="flex justify-between">
                  <Button variant="ghost" className="min-h-10 rounded-full px-3 text-[var(--roma-espresso)]/72 hover:bg-[var(--roma-linen)] sm:min-h-11" onClick={goBack}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                  <Button className="roma-cta min-h-10 rounded-full px-4 sm:min-h-11" onClick={goNext} disabled={!selectedTime}>
                    Continue
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </StepFooter>
            </div>
          )}

          {/* ── Step 4: Customer Details ── */}
          {step === "details" && (
            <div className={cn("space-y-1.5 sm:space-y-3", stepEnterClass)}>
              {/* Tappable recap so guests can fix any earlier step without fear */}
              <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-xs text-[var(--roma-espresso)]/72">
                <button
                  type="button"
                  onClick={() => setStep("party_size")}
                  className="font-medium text-[var(--roma-terracotta)] hover:underline"
                >
                  {partySize} {partySize === 1 ? "guest" : "guests"}
                </button>
                <span aria-hidden="true">&middot;</span>
                <button
                  type="button"
                  onClick={() => setStep("date")}
                  className="font-medium text-[var(--roma-terracotta)] hover:underline"
                >
                  {selectedDate
                    ? selectedDate.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      })
                    : "Pick a date"}
                </button>
                <span aria-hidden="true">&middot;</span>
                <button
                  type="button"
                  onClick={() => setStep("time")}
                  className="font-medium text-[var(--roma-terracotta)] hover:underline"
                >
                  {selectedTime ? formatTime12h(selectedTime) : "Pick a time"}
                </button>
              </div>
              <div className="text-center">
                <h3 className={compactStepTitleClass}>
                  Your details
                </h3>
                <p className={compactStepSubtitleClass}>
                  Almost there. Tell us who to expect.
                </p>
              </div>

              <div className="space-y-1 sm:space-y-3">
                {prefilledFromMemory && (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/40 px-3 py-2 animate-in fade-in duration-150 motion-reduce:animate-none">
                    <p className="text-xs text-[var(--roma-espresso)]/80">
                      Welcome back! We filled in your details.
                    </p>
                    <button
                      type="button"
                      onClick={handleClearGuestMemory}
                      className="shrink-0 text-xs font-medium text-[var(--roma-terracotta)] hover:underline"
                    >
                      Not you? Clear
                    </button>
                  </div>
                )}
                {/* Name */}
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="res-name" className={leadFieldLabelClass}>
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="res-name"
                    name="name"
                    autoComplete="name"
                    placeholder="John Smith"
                    value={customerName}
                    onChange={(e) => {
                      setCustomerName(e.target.value);
                      if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                    }}
                    aria-invalid={Boolean(errors.name)}
                    className={cn(leadInputClass, errors.name && leadInputErrorClass)}
                  />
                  {errors.name && (
                    <p className={leadFieldErrorClass}>{errors.name}</p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="res-phone" className={leadFieldLabelClass}>
                    Phone Number
                  </Label>
                  <Input
                    id="res-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="(702) 555-0123"
                    value={customerPhone}
                    inputMode="tel"
                    onChange={(e) => {
                      setCustomerPhone(formatPhoneInput(e.target.value));
                      if (errors.phone || errors.email === PHONE_OR_EMAIL_REQUIRED) {
                        setErrors((prev) => ({
                          ...prev,
                          phone: "",
                          email: prev.email === PHONE_OR_EMAIL_REQUIRED ? "" : prev.email,
                        }));
                      }
                    }}
                    aria-invalid={Boolean(errors.phone)}
                    className={cn(leadInputClass, errors.phone && leadInputErrorClass)}
                  />
                  {errors.phone && (
                    <p className={leadFieldErrorClass}>{errors.phone}</p>
                  )}
                  <p className="text-xs text-[var(--roma-espresso)]/72">
                    Used only if the host needs to confirm a detail about your table.
                  </p>
                </div>

                {/* Email */}
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="res-email" className={leadFieldLabelClass}>
                    Email
                  </Label>
                  <Input
                    id="res-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="john@example.com"
                    value={customerEmail}
                    spellCheck={false}
                    onChange={(e) => {
                      setCustomerEmail(e.target.value);
                      if (errors.email || errors.phone === PHONE_OR_EMAIL_REQUIRED) {
                        setErrors((prev) => ({
                          ...prev,
                          email: "",
                          phone: prev.phone === PHONE_OR_EMAIL_REQUIRED ? "" : prev.phone,
                        }));
                      }
                    }}
                    aria-invalid={Boolean(errors.email)}
                    className={cn(leadInputClass, errors.email && leadInputErrorClass)}
                  />
                  {errors.email && (
                    <p className={leadFieldErrorClass}>{errors.email}</p>
                  )}
                  <p className="text-xs text-[var(--roma-espresso)]/72">
                    We&apos;ll send your confirmation to this email.
                  </p>
                  <p className="text-xs text-[var(--roma-espresso)]/72">
                    A phone number or an email is required. One is enough.
                  </p>
                </div>

                <div className="space-y-1">
                  <p className={cn(leadFieldLabelClass, "text-[var(--roma-espresso)]/78")}>
                    Text updates (optional)
                  </p>
                  <label className={smsConsentClass}>
                    <Checkbox
                      checked={smsConsent}
                      onCheckedChange={(checked) => setSmsConsent(checked === true)}
                      className="mt-0.5 shrink-0 border-[var(--roma-parchment-line)] data-[state=checked]:border-[var(--roma-terracotta)] data-[state=checked]:bg-[var(--roma-terracotta)]"
                    />
                    {smsConsentCopy ? (
                      <span>
                        {smsConsentCopy.text}{" "}
                        <a href={smsConsentCopy.privacyHref} className="underline">
                          Privacy
                        </a>{" "}
                        ·{" "}
                        <a href={smsConsentCopy.termsHref} className="underline">
                          Terms
                        </a>
                      </span>
                    ) : (
                      <span>
                        Yes, text me reservation updates and reminders. Message and data rates may
                        apply, message frequency varies, and you can reply STOP to opt out at any
                        time. Texting is not live yet; we use email until it launches.
                      </span>
                    )}
                  </label>
                </div>

                {/* Occasion + Seating behind an optional disclosure */}
                <div className="space-y-1 sm:space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowPreferences((v) => !v)}
                    aria-expanded={preferencesOpen}
                    className="inline-flex min-h-9 items-center gap-1 text-xs font-medium text-[var(--roma-terracotta)] transition-colors hover:underline sm:text-sm"
                  >
                    Add preferences (optional)
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform duration-150",
                        preferencesOpen && "rotate-180",
                      )}
                    />
                  </button>
                  {preferencesOpen && (
                    <div className="grid grid-cols-1 gap-1.5 animate-in fade-in duration-150 motion-reduce:animate-none sm:grid-cols-2 sm:gap-4">
                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="res-occasion" className={leadFieldLabelClass}>Occasion</Label>
                        <Select
                          value={occasion}
                          onValueChange={(val) => setOccasion(val as Occasion)}
                        >
                          <SelectTrigger id="res-occasion" className={leadSelectClass}>
                            <SelectValue placeholder="Select occasion" />
                          </SelectTrigger>
                          <SelectContent>
                            {OCCASIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <Label htmlFor="res-seating" className={leadFieldLabelClass}>Seating</Label>
                        <Select
                          value={seatingPreference}
                          onValueChange={(val) => setSeatingPreference(val as SeatingPreference)}
                        >
                          <SelectTrigger id="res-seating" className={leadSelectClass}>
                            <SelectValue placeholder="Select seating" />
                          </SelectTrigger>
                          <SelectContent>
                            {SEATING_OPTIONS.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Special Requests */}
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="res-requests" className={leadFieldLabelClass}>
                    Special Requests{" "}
                    <span className={leadOptionalHintClass}>(optional)</span>
                  </Label>
                  <Textarea
                    id="res-requests"
                    name="specialRequests"
                    placeholder="Allergies, accessibility needs, high chair, etc."
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    maxLength={500}
                    rows={2}
                    className={cn(leadInputClass, "min-h-16 resize-none py-2 sm:min-h-28 sm:py-3")}
                  />
                </div>
              </div>

              <StepFooter>
                <div className="flex justify-between">
                  <Button variant="ghost" className="min-h-10 rounded-full px-3 text-[var(--roma-espresso)]/85 hover:bg-[var(--roma-linen)] sm:min-h-11" onClick={goBack}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                  <Button className="roma-cta min-h-10 rounded-full px-4 sm:min-h-11" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Booking…
                      </>
                    ) : (
                      "Confirm Reservation"
                    )}
                  </Button>
                </div>
              </StepFooter>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phone CTA */}
      <div className="hidden border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-3 text-center sm:block sm:p-4">
        <p className="text-xs leading-5 text-[var(--roma-espresso)]/72 sm:text-sm sm:leading-6">
          <Phone className="mr-1.5 inline-block h-4 w-4" />
          Prefer to book by phone? Call us at{" "}
          <a
            href={`tel:${restaurantPhone}`}
            className="inline-flex min-h-11 items-center font-semibold text-[var(--roma-espresso)] transition-colors hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.phoneClicked({
                location: "reservation_phone_cta",
                cta_text: "Call restaurant",
                destination_url: `tel:${restaurantPhone}`,
                lead_type: "reservation",
              })
            }
          >
            {restaurantPhoneDisplay}
          </a>
        </p>
      </div>
    </div>
  );
}
